import React from 'react';
import PropTypes from 'prop-types';
import Waypoint from 'react-waypoint';
import { Scrollbars } from 'react-custom-scrollbars';

import fullname from 'utils/fullname';

import ChatRoomAutoFinish from 'chats/ChatRoomAutoFinish';
import ChatMessage from 'chats/ChatMessage';
import ChatRoomStartButton from 'chats/ChatRoomStartButton';
import ChatMessageForm from 'chats/ChatMessageForm';
import Loader from 'components/Loader';
import SecondaryButton from 'styled/Buttons/SecondaryButton';

import Body from './Body';
import ErrorMessage from './ErrorMessage';
import DateDivider from './DateDivider';
import ErrorContainer from './ErrorContainer';
import ErrorMessageButton from './ErrorMessageButton';

export const formatDateForChatRoom = (date) => {
  if (new Date(date).getYear() === new Date().getYear()) {
    return new Date(date).toLocaleDateString('ru', {
      day: 'numeric',
      month: 'long',
    });
  }

  return new Date(date).toLocaleDateString('ru', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

class ChatRoomOpenBody extends React.PureComponent {
  scrollbars = React.createRef()

  componentDidUpdate(prevProps) {
    // первая загрузка сообщений
    if (Object.keys(this.props.messagesByHash).length > 0 && Object.keys(prevProps.messagesByHash).length === 0) {
      this.scrollbars.current.scrollToBottom();
    }

    // заблокировали или разблокировали чат
    if (this.props.locked !== prevProps.locked) {
      this.scrollbars.current.scrollToBottom();
    }
  }

  get messagesByDate() {
    // TODO: для скорости это можно делать при вставке, а не в рендере сообщений
    // группируем сообщения по датам
    const groups = Object.values(Object.values(this.props.messagesByHash).reduce((accumulator, message) => {
      const result = { ...accumulator };

      const date = new Date(message.createdAt * 1000).toDateString();
      if (!result[date]) {
        result[date] = { date, messages: [] };
      }

      result[date].messages.push(message);

      return result;
    }, {}));

    // сортируем сообщения в группах по sequence (https://jira.life-team.net/browse/BAZAA-345)
    const groupsWithSortedMessages = groups.map(group => ({
      ...group,
      messages: group.messages.sort((a, b) => {
        if (a.sequence > b.sequence) return 1;
        if (a.sequence < b.sequence) return -1;
        return 0;
      }),
    }));

    // сортируем группы сообщений
    return groupsWithSortedMessages.sort((a, b) => {
      if (new Date(a.date) > new Date(b.date)) return 1;
      if (new Date(a.date) < new Date(b.date)) return -1;
      return 0;
    });
  }

  get canSendMessage() {
    switch (this.props.roomType) {
      // в техчат можно писать сразу
      case 'tech_chat': return true;

      // в чат модератора можно писать только если он залочен на себя
      case 'moderator_chat':
        return this.lockedByMe;

      default: return false;
    }
  }

  get lockedByOther() {
    return this.props.locked && this.props.lockedEmployeeId !== this.props.profileId;
  }

  get lockedByMe() {
    return this.props.locked && this.props.lockedEmployeeId === this.props.profileId;
  }

  get isChatExist() {
    return Boolean(this.props.id);
  }

  render() {
    return (
      <Scrollbars ref={this.scrollbars}>
        <Body>
          {this.props.loading && <Loader />}

          {!this.props.loading && !this.props.errors.length && this.props.next && (
            <Waypoint onEnter={this.props.onWaypointEnter} />
          )}

          {this.props.errors.length > 0 && (
            <ErrorContainer>
              {this.props.errors.map(message => (
                <ErrorMessage key={message} role="alert">{message}</ErrorMessage>
              ))}

              <SecondaryButton type="button" onClick={this.props.onReloadClick}>Обновить</SecondaryButton>
            </ErrorContainer>
          )}

          {!this.props.loading && !this.props.errors.length && Object.keys(this.props.messagesByHash).length === 0 && (
            <ErrorContainer>
              <ErrorMessage role="alert">Сообщений нет</ErrorMessage>
            </ErrorContainer>
          )}

          {this.messagesByDate.map(currentDate => (
            <React.Fragment key={currentDate.date}>
              <DateDivider>{formatDateForChatRoom(currentDate.date)}</DateDivider>

              {currentDate.messages.map(message => (
                <ChatMessage
                  key={message.id}
                  username={message.byUser ? fullname(message.user) : fullname(message.employee)}
                  avatarUrl={message.byUser
                    ? (message.user && message.user.avatar && message.user.avatar.url)
                    : (message.employee && message.employee.avatar && message.employee.avatar.url)
                  }
                  text={message.text}
                  time={message.createdAt * 1000}
                />
              ))}
            </React.Fragment>
          ))}

          {this.canSendMessage ? (
            <React.Fragment>
              <ChatMessageForm chatRoomId={this.props.id} onSended={this.props.onMessageSended} />

              {this.lockedByMe && (
                <ChatRoomAutoFinish
                  id={this.props.id}
                  lastCreatedAt={Object.values(this.props.messagesByHash).reduce((max, message) => (
                    max < message.createdAt ? message.createdAt : max
                  ), 0)}
                  userId={this.props.userId}
                  onFinished={this.props.onChatRoomFinished}
                />
              )}
            </React.Fragment>
          ) : (
            <React.Fragment>
              {this.lockedByOther && (
                <ErrorMessageButton role="alert">
                  Общается с&nbsp;

                  {this.props.lockedEmployeeFullname || 'другим модератором'}
                </ErrorMessageButton>
                )
              }

              {!this.isChatExist && (
                <ErrorMessageButton role="alert">Пользователь должен сам начать чат</ErrorMessageButton>
              )}

              <ChatRoomStartButton
                id={this.props.id}
                onStarted={this.props.onChatRoomStarted}
                disabled={!this.isChatExist || this.lockedByOther}
                autoFocus={this.props.startChatAutoFocus}
              />
            </React.Fragment>
          )}
        </Body>
      </Scrollbars>
    );
  }
}

ChatRoomOpenBody.propTypes = {
  id: PropTypes.string,

  startChatAutoFocus: PropTypes.bool,
  roomType: PropTypes.string,
  locked: PropTypes.bool,
  lockedEmployeeId: PropTypes.string,
  lockedEmployeeFullname: PropTypes.string,
  userId: PropTypes.string,
  profileId: PropTypes.string,

  messagesByHash: PropTypes.shape({}),
  errors: PropTypes.arrayOf(PropTypes.string),
  loading: PropTypes.bool,
  next: PropTypes.bool,

  onWaypointEnter: PropTypes.func,
  onReloadClick: PropTypes.func,
  onChatRoomStarted: PropTypes.func,
  onChatRoomFinished: PropTypes.func,
  onMessageSended: PropTypes.func,
};

ChatRoomOpenBody.defaultProps = {
  id: '',

  startChatAutoFocus: false,
  roomType: '',
  locked: false,
  lockedEmployeeId: '',
  lockedEmployeeFullname: '',
  userId: '',
  profileId: '',

  messagesByHash: {},
  errors: [],
  loading: true,
  next: false,

  onWaypointEnter: () => {},
  onReloadClick: () => {},
  onChatRoomStarted: () => {},
  onChatRoomFinished: () => {},
  onMessageSended: () => {},
};

export default ChatRoomOpenBody;
